import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import {
  formatJobDate,
  getJobSalaryLabel,
  getJobTitle,
  getMyJobApplications,
} from "../api/jobs";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد المراجعة" : "Pending" },
    reviewed: { className: "text-bg-info", label: isAr ? "تمت المراجعة" : "Reviewed" },
    shortlisted: { className: "text-bg-primary", label: isAr ? "في القائمة المختصرة" : "Shortlisted" },
    rejected: { className: "text-bg-danger", label: isAr ? "مرفوض" : "Rejected" },
    accepted: { className: "text-bg-success", label: isAr ? "مقبول" : "Accepted" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function MyJobApplications({ embedded = false }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await getMyJobApplications();
        if (active) {
          setApplications(data);
        }
      } catch (err) {
        logger.error("My job applications error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل طلباتك." : "Unable to load your applications.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  return (
    <div className={embedded ? "" : "container py-4 py-lg-5"} dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className={`${embedded ? "h3" : ""} fw-bold mb-1`}>
            {isAr ? "وظائفي المتقدم لها" : "My Job Applications"}
          </h1>
          <p className="text-muted mb-0">
            {isAr
              ? "تابع حالة كل طلب قدمته، وراجع ملاحظات الإدارة إن وجدت."
              : "Track the status of every job you applied for and review admin notes when available."}
          </p>
        </div>
        <Link to="/jobs" className="btn btn-outline-dark rounded-pill px-4">
          {isAr ? "تصفح الوظائف" : "Browse Jobs"}
        </Link>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل طلباتك..." : "Loading your applications..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : applications.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد طلبات توظيف بعد" : "You have not applied for any jobs yet"}
            </h2>
            <p className="text-muted mb-4">
              {isAr
                ? "ابدأ من صفحة الوظائف واختر الفرصة المناسبة لك."
                : "Start from the jobs page and choose the right opportunity for you."}
            </p>
            <Link to="/jobs" className="btn btn-primary rounded-pill px-4">
              {isAr ? "عرض الوظائف" : "View Jobs"}
            </Link>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {applications.map((application) => {
            const job = application.job || {};
            const statusMeta = getStatusMeta(application.status, isAr);

            return (
              <div className="col-12 col-lg-6" key={application.id}>
                <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                  <div className="row g-0 h-100">
                    <div className="col-md-4">
                      <img
                        src={
                          job?.cover_image ||
                          "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80"
                        }
                        alt={getJobTitle(job, isAr)}
                        className="course-my-card__image"
                      />
                    </div>

                    <div className="col-md-8">
                      <div className="card-body h-100 d-flex flex-column">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                          <span className="badge text-bg-light">
                            {job?.employment_type || (isAr ? "غير محدد" : "Not specified")}
                          </span>
                        </div>

                        <h2 className="h5 fw-bold mb-2">{getJobTitle(job, isAr)}</h2>
                        <div className="small text-muted d-grid gap-2 mb-3">
                          <div>
                            {isAr ? "الشركة:" : "Company:"} {job?.company || "Mousa Group"}
                          </div>
                          <div>
                            {isAr ? "الراتب:" : "Salary:"} {getJobSalaryLabel(job, isAr)}
                          </div>
                          <div>
                            {isAr ? "قدمت بتاريخ:" : "Applied on:"}{" "}
                            {application?.applied_at
                              ? formatJobDate(application.applied_at, lang)
                              : isAr
                                ? "غير متوفر"
                                : "N/A"}
                          </div>
                        </div>

                        {application?.admin_notes ? (
                          <div className="alert alert-light border rounded-4 small mb-3">
                            <strong>{isAr ? "ملاحظة الإدارة:" : "Admin note:"}</strong>{" "}
                            {application.admin_notes}
                          </div>
                        ) : null}

                        <div className="d-flex flex-wrap gap-2 mt-auto">
                          <Link
                            to={`/jobs/${job?.slug || job?.id}`}
                            className="btn btn-primary rounded-pill px-4"
                          >
                            {isAr ? "عرض الوظيفة" : "View Job"}
                          </Link>
                          {application?.cv_url ? (
                            <a
                              href={application.cv_url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-dark rounded-pill"
                            >
                              {isAr ? "فتح السيرة" : "Open CV"}
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
